import styled from "@emotion/styled";
import ArrowDropUpSharpIcon from "@mui/icons-material/ArrowDropDownSharp";
import CircleIcon from "@mui/icons-material/Circle";
import { Box, List, ListItem } from "@mui/material";
import { FC, useState } from "react";
import { NavLink, NavLinkProps } from "react-router-dom";
import { ISidebarLinkProps } from "src/shared/api/sideMenuLinks/types";
import { IconComponent } from "src/shared/components";

const StyledCounters = styled("span")(`
    display: flex;
    align-items: center;
`);

const StyledCounter = styled("span")<{ color?: string }>(
  ({ color }) => `
    background-color: ${color || "#00b9ff"};
    border-radius: 10px;
    color: #fff;
    display: inline-block;
    font-size: 10px;
    font-weight: 400;
    line-height: 12px;
    padding: 1px 4px 0;
    vertical-align: middle;
    margin-left: 4px;
`,
);

const StyledExpandButton = styled("button")(`
    width: 48px;
    position: absolute;
    right: 0;
    top: 0;
    height: 100%;
    text-align: center;
    cursor: pointer;
    background-color: transparent;
    outline: none;
    border: none;
    color: white;

    &:hover, &:active {
        background-color: transparent;
    }

    svg {
        font-size: 12px;
        &.rotated {
            transform: rotate(180deg);
        }
    }
`);

const StyledBox = styled(Box)(`
    min-width: 236px;
    position: relative;
    width: 100%;

    &.opened {
        background-color: #1d283a;
    }

    &:hover {
        background-color: #1d283a;
    }
`);

const StyledLink = styled(NavLink)(`
    text-decoration: none;
    font-size: 12px;
    line-height: 16px;
    color: white;
    padding: 16px 16px 16px 36px;
    display: flex;
    align-items: center;

    &:hover, &:active, &.active {
        background-color: #1d283a;
    }

    &.link-child{
        flex-grow: 1;
    }

    svg {
        font-size: 16px;
        opacity: 0.7;
        margin-right: 16px;

        &.dot {
            opacity: 1;
            font-size: 5px;
            margin-left: -22px;
        }
    }
`);

const StyledAnchor = styled.a(`
    text-decoration: none;
    font-size: 12px;
    line-height: 16px;
    color: white;
    padding: 16px 16px 16px 36px;
    display: flex;
    align-items: center;

    &:hover, &:active, &.active {
        background-color: #1d283a;
    }

    &.link-child{
        flex-grow: 1;
    }

    svg {
        width: 17px;
        height: 17px;
        opacity: 0.7;
        margin-right: 16px;

        &.dot {
            width: 5px;
            height: 5px;
            opacity: 1;
            font-size: 5px;
            margin-left: -22px;
        }
    }
`);

const StyledListItem = styled(ListItem)(`
    align-items: start;
    flex-direction: column;
    border-top: 1px solid #3d485a;
    padding: 0;

    &.child {
        border-top: none;
        flex-direction: row;
    }
`);

export const SidebarLink: FC<ISidebarLinkProps> = ({
  children,
  icon_type,
  uri,
  text,
  isExternal,
  count,
  counters,
  ...rest
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const hasSubLinks = children && children.length > 0;

  const navLinkProps = {
    ...rest,
    to: uri,
    iconname: icon_type,
  } as NavLinkProps;

  const shouldShowCounter = typeof count === "number" && count > 0;

  const counterElement = shouldShowCounter ? (
    <StyledCounter>{count}</StyledCounter>
  ) : null;

  const visibleCounters = counters?.filter(
    ({ value }) => typeof value === "number" && value > 0,
  );

  const countersElement = visibleCounters?.length ? (
    <StyledCounters>
      {visibleCounters.map(({ key, value, color }) => (
        <StyledCounter key={key} color={color}>
          {value}
        </StyledCounter>
      ))}
    </StyledCounters>
  ) : null;

  return (
    <StyledListItem>
      <StyledBox className={isOpen ? "opened" : undefined}>
        {isExternal ? (
          <StyledAnchor href={uri} title={text}>
            {icon_type && (
              <IconComponent
                iconname={icon_type}
                sx={{ opacity: 0.7, height: 17, width: 17 }}
              />
            )}
            {text}
            {counterElement}
            {countersElement}
          </StyledAnchor>
        ) : (
          <StyledLink {...navLinkProps} title={text} end>
            {icon_type && (
              <IconComponent
                iconname={icon_type}
                sx={{ opacity: 0.7, height: 17, width: 17 }}
              />
            )}
            {text}
            {counterElement}
            {countersElement}
          </StyledLink>
        )}

        {hasSubLinks && (
          <StyledExpandButton onClick={() => setIsOpen(!isOpen)}>
            <ArrowDropUpSharpIcon className={isOpen ? "rotated" : undefined} />
          </StyledExpandButton>
        )}
      </StyledBox>

      {hasSubLinks && isOpen && (
        <List
          sx={{
            padding: 0,
            borderTop: "1px solid #3d485a",
            backgroundColor: "#313c4e",
            width: "100%",
          }}
        >
          {children.map(
            ({
              text,
              uri,
              icon_type,
              isExternal,
              count,
              counters,
              ...rest
            }) => {
              const childLinkProps = {
                ...rest,
                to: uri,
                iconname: icon_type,
              } as NavLinkProps;

              const shouldShowChildCounter =
                typeof count === "number" && count > 0;

              const childCountersElement = counters?.length ? (
                <StyledCounters>
                  {counters.map(({ key, value, color }) => (
                    <StyledCounter key={key} color={color}>
                      {value}
                    </StyledCounter>
                  ))}
                </StyledCounters>
              ) : null;

              return (
                <StyledListItem key={String(rest.to || text)} className="child">
                  {isExternal ? (
                    <StyledAnchor
                      className="link-child"
                      href={uri}
                      title={text}
                    >
                      <CircleIcon className="dot" />
                      {text}
                      {shouldShowChildCounter && (
                        <StyledCounter>{count}</StyledCounter>
                      )}
                      {childCountersElement}
                    </StyledAnchor>
                  ) : (
                    <StyledLink
                      className="link-child"
                      title={text}
                      {...childLinkProps}
                      end
                    >
                      <CircleIcon className="dot" />
                      {text}
                      {shouldShowChildCounter && (
                        <StyledCounter>{count}</StyledCounter>
                      )}
                      {childCountersElement}
                    </StyledLink>
                  )}
                </StyledListItem>
              );
            },
          )}
        </List>
      )}
    </StyledListItem>
  );
};
